import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Phone, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import useFreshData from "@/lib/useFreshData";
import SEO, { breadcrumbSchema } from "@/components/seo/SEO";

const CATEGORIES = [
    { id: "all", label: "Todos" },
    { id: "hotel", label: "Hoteles" },
    { id: "hostal", label: "Hostales" },
    { id: "apartamento", label: "Apartamentos" },
    { id: "camping", label: "Campings" },
];

const CATEGORY_LABEL = {
    hotel: "Hotel",
    camping: "Camping",
    hostal: "Hostal",
    apartamento: "Apartamento",
    otro: "Alojamiento",
};

export default function Directory() {
    const [category, setCategory] = useState("all");
    const [q, setQ] = useState("");
    const { data: items, loading } = useFreshData(
        async () => {
            const r = await api.get("/directory", {
                params: category === "all" ? {} : { category },
            });
            return r.data || [];
        },
        { initialData: [], deps: [category] }
    );

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        const list = items || [];
        if (!s) return list;
        return list.filter(
            (m) =>
                m.name.toLowerCase().includes(s) ||
                (m.description || "").toLowerCase().includes(s)
        );
    }, [items, q]);

    return (
        <div data-testid="directory-page" className="bg-white">
            <SEO
                title="Directorio de alojamientos turísticos de Tarifa"
                description="Directorio oficial de hoteles, hostales, campings y apartamentos turísticos de Tarifa asociados a ASATTA. Encuentra el alojamiento perfecto en Tarifa."
                path="/directorio"
                jsonLd={breadcrumbSchema([
                    { name: "Inicio", path: "/" },
                    { name: "Directorio", path: "/directorio" },
                ])}
            />

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 sm:pt-24">
                <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                    Directorio de socios
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#082050] tracking-tight">
                    Alojamientos turísticos de Tarifa.
                </h1>
                <p className="mt-4 text-lg text-[#4A5568] max-w-2xl">
                    Descubre los hoteles, hostales, campings y apartamentos turísticos de
                    Tarifa asociados a ASATTA. Cada alojamiento cuenta con su ficha
                    completa: descripción, ubicación y datos de contacto oficiales.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
                <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between border-t border-b border-[#E2E4E9] py-4">
                    <div className="flex flex-wrap gap-2">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setCategory(c.id)}
                                data-testid={`filter-${c.id}`}
                                className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${
                                    category === c.id
                                        ? "bg-[#082050] text-white border-[#082050]"
                                        : "border-[#E2E4E9] text-[#4A5568] hover:border-[#082050]"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <input
                        data-testid="directory-search"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar alojamiento..."
                        aria-label="Buscar alojamiento por nombre"
                        className="border border-[#E2E4E9] px-4 py-2 text-sm w-full md:w-72 focus:outline-none focus:border-[#082050]"
                    />
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-24">
                {loading && (
                    <div data-testid="directory-loading" className="py-20 text-center text-[#4A5568]">
                        Cargando socios...
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <div data-testid="directory-empty" className="py-20 text-center text-[#4A5568]">
                        No se encontraron alojamientos.
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((m, i) => {
                        const categoryLbl = CATEGORY_LABEL[m.category] || m.category;
                        const detailUrl = m.slug ? `/directorio/${m.slug}` : null;
                        return (
                            <article
                                key={m.id || i}
                                data-testid={`directory-item-${i}`}
                                className="border border-[#E2E4E9] hover:border-[#082050] transition-colors flex flex-col group"
                            >
                                {detailUrl ? (
                                    <Link to={detailUrl} aria-label={`Ver ficha de ${m.name}`}>
                                        {m.logo && (
                                            <div className="aspect-[16/10] overflow-hidden bg-[#F8F9FA]">
                                                <img
                                                    src={m.logo}
                                                    alt={`${m.name}, ${categoryLbl.toLowerCase()} en ${m.location || "Tarifa"}`}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                />
                                            </div>
                                        )}
                                    </Link>
                                ) : (
                                    m.logo && (
                                        <div className="aspect-[16/10] overflow-hidden bg-[#F8F9FA]">
                                            <img
                                                src={m.logo}
                                                alt={`${m.name}, ${categoryLbl.toLowerCase()} en ${m.location || "Tarifa"}`}
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    )
                                )}
                                <div className="p-6 flex flex-col flex-1">
                                    <div className="text-[10px] uppercase tracking-widest text-[#082050]/60">
                                        {categoryLbl}
                                    </div>
                                    <h3 className="mt-2 text-lg font-semibold text-[#082050] tracking-tight">
                                        {detailUrl ? (
                                            <Link
                                                to={detailUrl}
                                                className="hover:underline"
                                                data-testid={`directory-link-${i}`}
                                            >
                                                {m.name}
                                            </Link>
                                        ) : (
                                            m.name
                                        )}
                                    </h3>
                                    {m.description && (
                                        <p className="mt-3 text-sm text-[#4A5568] leading-relaxed line-clamp-3">
                                            {m.description}
                                        </p>
                                    )}
                                    <div className="mt-4 text-xs text-[#718096]">{m.location}</div>
                                    <div className="mt-4 pt-4 border-t border-[#E2E4E9] flex items-center gap-4 text-xs text-[#4A5568]">
                                        {m.website && (
                                            <a
                                                href={m.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 hover:text-[#082050]"
                                            >
                                                <Globe size={12} /> Web
                                            </a>
                                        )}
                                        {m.phone && (
                                            <a
                                                href={`tel:${m.phone.replace(/\s/g, "")}`}
                                                className="inline-flex items-center gap-1 hover:text-[#082050]"
                                            >
                                                <Phone size={12} /> {m.phone}
                                            </a>
                                        )}
                                        {detailUrl && (
                                            <Link
                                                to={detailUrl}
                                                className="ml-auto inline-flex items-center gap-1 text-[#082050] font-medium"
                                            >
                                                Ver ficha
                                                <ArrowRight size={12} />
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
